'use client';
import { useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSupabase } from '~/lib/supabase/browser';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '~/components/ui/form';
const schema = z.object({ code: z.string().regex(/^\d{6}$/, 'Enter the six-digit code') });
export function Mfa({
  factors,
  challenge = false,
  onVerified,
  returnTo = '/home',
}: {
  factors: { id: string; friendly_name?: string }[];
  challenge?: boolean;
  onVerified?: () => void;
  returnTo?: string;
}) {
  const client = useSupabase();
  const router = useRouter();
  const [state, setState] = useState({
    factorId: factors[0]?.id ?? '',
    qr: '',
    secret: '',
    error: '',
    pending: false,
  });
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { code: '' } });
  async function enroll() {
    setState((s) => ({ ...s, pending: true, error: '' }));
    const r = await client.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Authenticator' });
    if (r.error) {
      setState((s) => ({ ...s, pending: false, error: r.error.message }));
      return;
    }
    setState({
      factorId: r.data.id,
      qr: r.data.totp.qr_code.trim(),
      secret: r.data.totp.secret,
      error: '',
      pending: false,
    });
  }
  async function verify({ code }: z.infer<typeof schema>) {
    setState((s) => ({ ...s, pending: true, error: '' }));
    const r = await client.auth.mfa.challengeAndVerify({ factorId: state.factorId, code });
    if (r.error) {
      setState((s) => ({
        ...s,
        pending: false,
        error: 'The code could not be verified. Try the latest code.',
      }));
      return;
    }
    setState((s) => ({ ...s, pending: false, qr: '', secret: '' }));
    if (onVerified) onVerified();
    else if (challenge) router.replace(returnTo);
    router.refresh();
  }
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold">
        {challenge ? 'Verify your sign-in' : 'Two-step verification'}
      </h2>
      {state.qr && (
        <>
          <p>Scan this code in your authenticator app, then enter its six-digit code.</p>
          <Image
            src={state.qr}
            alt="Authenticator setup QR code"
            width={200}
            height={200}
            unoptimized
          />
          <details>
            <summary>Enter a setup key instead</summary>
            <code className="break-all">{state.secret}</code>
          </details>
        </>
      )}
      {challenge || state.qr ? (
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(verify)}>
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Authentication code</FormLabel>
                  <FormControl>
                    <Input {...field} inputMode="numeric" autoComplete="one-time-code" autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={state.pending}>
              Verify
            </Button>
          </form>
        </Form>
      ) : factors.length ? (
        <div className="space-y-3">
          <p>Your account is protected by an authenticator.</p>
          {factors.map((f) => (
            <Button
              key={f.id}
              variant="outline"
              disabled={state.pending}
              onClick={async () => {
                setState((s) => ({ ...s, pending: true }));
                const r = await client.auth.mfa.unenroll({ factorId: f.id });
                setState((s) => ({ ...s, pending: false, error: r.error?.message ?? '' }));
                router.refresh();
              }}
            >
              Remove {f.friendly_name ?? 'authenticator'}
            </Button>
          ))}
        </div>
      ) : (
        <>
          <p>Add an authenticator for an extra step when you sign in.</p>
          <Button onClick={enroll} disabled={state.pending}>
            Set up authenticator
          </Button>
        </>
      )}
      {state.error && (
        <p role="alert" className="text-destructive">
          {state.error}
        </p>
      )}
    </section>
  );
}
